import React from 'react';
import { useEffect, useMemo, useState } from "react";
import { useStoreState, useStoreActions } from "easy-peasy";
import { types } from "../lib/store";
import {useTranslation} from "react-i18next";

import {
  Label,
  NumericInput,
  Position
} from "@blueprintjs/core";

function DesignPoint() {
  const { t } = useTranslation();

  const designPoint = useStoreState(state => state.selection.designPoint);
  const { temperatures } = useStoreState(state => state.selection.servicePoint);
  const defaultTemperatures = useStoreState(state => state.selection.temperatures);
  const units = useStoreState(state => state.selection.units);

  const updateStorePoint = useStoreActions(actions => actions.selection[types.SET_DESIGN_POINT]);

  const [point, setPoint] = useState(designPoint);
  const [pointString, setPointString] = useState(designPoint);

  const updateLocalState = (inputName, valueAsNumber, valueAsString) => {
    const newValue = inputName === "density_mode" ? valueAsString : (isNaN(valueAsNumber) ? 0 : valueAsNumber);

    setPoint({
      ...point,
      [inputName]: newValue
    });

    setPointString({
      ...pointString,
      [inputName]: valueAsString
    });
  };

  const updateStore = (e) => {
    e.preventDefault();

    updateStorePoint(point);
  };

  useEffect(() => {
    setPoint(designPoint);
    setPointString(designPoint);
  }, [designPoint]);

  const showTempWarning = useMemo(() => {
    // convert temperature to celsius

    let temperature = parseFloat(point.air_max_temperature)

    switch (String(units.temperature)) {
      case "14": // ºC
        // do nothing, we already have the temperature in celsius
        break;
      case "15": // ºK
        // convert from Kelvin to Celsius
        temperature = temperature - 273.15;
        break;
      case "16": // ºF
        // convert from Fahrenheit to Celsius
        temperature = (temperature - 32) / 1.8;
        break;
    }

    const minTemperature = temperatures ? temperatures.min : defaultTemperatures.min;
    const maxTemperature = temperatures ? temperatures.max : defaultTemperatures.max;

    if (minTemperature !== undefined && temperature < minTemperature) {
      return true;
    }
    else if (maxTemperature === undefined && temperature > 200){
      return true;
    }
    else if (maxTemperature !== undefined && temperature > maxTemperature) {
      return true;
    }
    else {
      return false;
    }
  }, [point, temperatures, defaultTemperatures]);

  return (
    <>
      <div className="panel panel-info">
        <div className="panel-heading">
          { t("frontend:fan.design_point") }
        </div>
        <div className="panel-body">
          <form id="dp_form" className="form-horizontal" onSubmit={updateStore}>
            <div className="form-group form-group-sm">
              <label htmlFor="dp_flow" className="col-sm-6 control-label">Q ({ t(`units:${units.flow}`) })</label>
              <div className="col-sm-6">
                <NumericInput minorStepSize={0.01} stepSize={0.01} fill={true} allowNumericCharactersOnly={true} buttonPosition="none" min={0} value={pointString.flow} onValueChange={(valueAsNumber, valueAsString) => updateLocalState("flow", valueAsNumber, valueAsString)} />
              </div>
            </div>
            <div className="form-group form-group-sm">
              <label htmlFor="dp_pressure" className="col-sm-6 control-label">Ps ({ t(`units:${units.pressure}`) })</label>
              <div className="col-sm-6">
                <NumericInput minorStepSize={0.01} stepSize={0.01} fill={true} allowNumericCharactersOnly={true} buttonPosition="none" min={0} value={pointString.pressure} onValueChange={(valueAsNumber, valueAsString) => updateLocalState("pressure", valueAsNumber, valueAsString)} />
              </div>
            </div>
            <div className="form-group form-group-sm">
              <label htmlFor="dp_density_mode" className="col-sm-6 control-label">{ t('frontend:selection.density_mode') }</label>
              <div className="col-sm-6">
                <select name="dp_density_mode" className="form-control" onChange={(e) => updateLocalState("density_mode", e.target.value, e.target.value)} value={pointString.density_mode}>
                  <option key="calculated" value="calculated">{ t('frontend:selection.calculated') }</option>
                  <option key="user_defined" value="user_defined">{ t('frontend:selection.user_defined') }</option>
                </select>
              </div>
            </div>
            { pointString.density_mode == "calculated" && <div className="form-group form-group-sm">
              <label htmlFor="dp_temperature" className="col-sm-6 control-label">{ t('frontend:user_preferences.temperature') } ({ t(`units:${units.temperature}`) })</label>
              <div className="col-sm-6">
                <NumericInput minorStepSize={0.01} stepSize={0.01} fill={true} allowNumericCharactersOnly={true} buttonPosition="none" min={0} value={pointString.temperature} onValueChange={(valueAsNumber, valueAsString) => updateLocalState("temperature", valueAsNumber, valueAsString)} />
              </div>
            </div> }
            { pointString.density_mode == "calculated" && <div className="form-group form-group-sm">
              <label htmlFor="dp_height" className="col-sm-6 control-label">{ t('frontend:fan.sea_level') } ({ t(`units:${units.distance}`) })</label>
              <div className="col-sm-6">
                <NumericInput minorStepSize={0.01} stepSize={0.01} fill={true} allowNumericCharactersOnly={true} buttonPosition="none" min={0} value={pointString.height} onValueChange={(valueAsNumber, valueAsString) => updateLocalState("height", valueAsNumber, valueAsString)} />
              </div>
            </div> }
            { pointString.density_mode == "user_defined" && <div className="form-group form-group-sm">
              <label htmlFor="dp_density" className="col-sm-6 control-label">{ t('frontend:selection.density') } (Kg/m³)</label>
              <div className="col-sm-6">
                <NumericInput minorStepSize={0.01} stepSize={0.01} fill={true} allowNumericCharactersOnly={true} buttonPosition="none" min={0} value={pointString.density} onValueChange={(valueAsNumber, valueAsString) => updateLocalState("density", valueAsNumber, valueAsString)} />
              </div>
            </div> }
            <div className="form-group form-group-sm">
              <label htmlFor="dp_air_max_temperature" className="col-sm-6 control-label">{ t('frontend:selection.air_max_temperature') } ({ t(`units:${units.temperature}`) })</label>
              <div className="col-sm-6">
                <NumericInput minorStepSize={0.01} stepSize={0.01} fill={true} allowNumericCharactersOnly={true} buttonPosition="none" min={0} value={pointString.air_max_temperature} onValueChange={(valueAsNumber, valueAsString) => updateLocalState("air_max_temperature", valueAsNumber, valueAsString)} />
              </div>
            </div>
            <div className="text-center">
              <button type="submit" className="btn btn-primary">{ t('frontend:send') }</button>
            </div>
          </form>
        </div>
      </div>
      {
        showTempWarning &&
        <div id="temperature-warning-panel" className="panel panel-danger">
          <div className="panel-heading">
            { t('frontend:fan.attention') }
          </div>
          <div className="panel-body text-center">
            { t('frontend:fan.check') }
          </div>
        </div>
      }
    </>
  );
}

export default DesignPoint;
